import { UserPermission } from '../../services/permission.service';

export interface NavTab {
  readonly label: string;
  readonly route: string;
}

/**
 * The tab set for a role, in navbar order.
 *
 * This lives outside the navbar component because the breadcrumb is built on
 * the same list. A breadcrumb crumb that leads to a page the navbar also links
 * to must use the navbar's wording for it — when the two were written out
 * separately, a crumb reading "Dashboard" led to the tab labelled
 * "Home & Search", and a crumb reading "PAC Feed Worklist" led to the tab
 * labelled "PAC Feed". Same page, two names, in two navigations shown one
 * above the other.
 *
 * The first entry is the role's home, which is where every breadcrumb starts.
 */
export function navTabsFor(
  permission: UserPermission | null,
  academicOfficer: { emp_id?: string } | null,
): NavTab[] {
  if (!permission) return [];

  // Academic Officers get only their own dashboard. Without a loaded officer
  // record there is no emp_id to point it at, so fall through to the default
  // tab set until the record arrives.
  if (permission.isAcademicOfficer && academicOfficer) {
    return [{ label: 'Dashboard', route: `/academic-officer/${academicOfficer.emp_id}` }];
  }

  // The Vice Provost for Faculty Affairs reviews leave requests and nothing
  // else — no officer search, PAC feed, reports, or user management — so it
  // gets its own minimal tab set and a plain "Home" label (its dashboard has
  // no search on it).
  if (permission.groups.includes('vice_provost_faculty_affairs')) {
    return [
      { label: 'Home', route: '/dashboard' },
      { label: 'Leave Requests', route: '/admin-leave-requests' },
    ];
  }

  const tabs: NavTab[] = [{ label: 'Home & Search', route: '/dashboard' }];

  // Groups that get the full Academic-Appointment tab set. AAU, AAU-CUIMC and
  // AAA-CUIMC see the same items as AAA (only the "Users" tab differs below).
  const adminGroups = ['academic_appoint_admin'];
  const appointmentGroups = [...adminGroups, 'academic_appoint_user', 'academic_appoint_user_cuimc'];
  // Reporting-unit / department scoped reviewers (also when impersonating them).
  const scopedReviewGroups = ['school_institute_libraries_level', 'department_center_level'];

  if (permission.groups.some(g => appointmentGroups.includes(g))) {
    tabs.push(
      { label: 'PAC Feed', route: '/pac-feed' },
      { label: 'Leave Requests', route: '/admin-leave-requests' },
      { label: 'Advanced Officer Search', route: '/academic-officer-search' },
      { label: 'Reports', route: '/report' },
    );
    // "Users" (management) is limited to AAA and AAA-CUIMC.
    if (permission.groups.some(g => adminGroups.includes(g))) {
      tabs.push({ label: 'Users', route: '/user-management' });
    }
  } else if (permission.groups.some(g => scopedReviewGroups.includes(g))) {
    // School/Center and Department users act within their own scope: review
    // leave, search officers, and view reports.
    tabs.push(
      { label: 'Leave Requests', route: '/admin-leave-requests' },
      { label: 'Advanced Officer Search', route: '/academic-officer-search' },
      { label: 'Reports', route: '/report' },
    );
  }

  return tabs;
}
